import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import TopNav from "@/components/layout/TopNav";
import Sidebar from "@/components/layout/Sidebar";
import Dashboard from "@/pages/Dashboard";
import SetDetail from "@/pages/SetDetail";
import Scans from "@/pages/Scans";
import Watchlists from "@/pages/Watchlists";
import Invoices from "@/pages/Invoices";
import DealsTable from "@/pages/DealsTable";
import Identify from "@/pages/Identify";
import Settings from "@/pages/Settings";
import RequestDetail from "@/pages/RequestDetail";
import Anomalies from "@/pages/Anomalies";
import SiteUsers from "@/pages/SiteUsers";
import Invoice from "@/pages/public/Invoice";
import Account, { CustomerAuthCallback } from "@/pages/public/Account";
import Offers4Bricks from "@/pages/public/Offers4Bricks";
import { AuthGate, AuthCallback, AdminLogin } from "@/components/auth/AdminAuth";

/* Two sites, one deployment:
   /            → OFFERS4BRICKS (public, customers)
   /admin/...   → BrickSource (admin, Google-gated). BrowserRouter basename keeps
                  every existing internal link ("/deals", "/set/123") untouched. */

function Shell() {
  return (
    <div className="min-h-screen bg-[#FFFFFF] text-gray-900 flex flex-col" data-testid="app-shell">
      <TopNav />
      <div className="flex flex-1">
        <Sidebar />
        <main className="flex-1 overflow-x-hidden" data-testid="main-content">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/set/:setNumber" element={<SetDetail />} />
            <Route path="/scans" element={<Scans />} />
            <Route path="/watchlists" element={<Watchlists />} />
            <Route path="/collection" element={<Navigate to="/" replace />} />
            <Route path="/invoices" element={<Invoices />} />
            <Route path="/invoices/:id" element={<RequestDetail backTo="/invoices" />} />
            <Route path="/deals" element={<Navigate to="/invoices" replace />} />
            <Route path="/deals/table" element={<DealsTable />} />
            <Route path="/deals/:id" element={<RequestDetail backTo="/invoices" />} />
            <Route path="/identify" element={<Identify />} />
            <Route path="/anomalies" element={<Anomalies />} />
            <Route path="/site-users" element={<SiteUsers />} />
            <Route path="/offers4bricks" element={<Navigate to="/settings" replace />} />
            <Route path="/offers4bricks/requests/:id" element={<RequestDetail />} />
            <Route path="/settings" element={<Settings />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

function AdminRouter() {
  const location = useLocation(); // hash read here (reactive), not window.location
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<AdminLogin />} />
      <Route path="/invoice/:id" element={<AuthGate><RequestDetail standalone /></AuthGate>} />
      <Route path="/*" element={<AuthGate><Shell /></AuthGate>} />
    </Routes>
  );
}

function PublicRouter() {
  const location = useLocation(); // read the hash here (reactive), not window.location
  if (location.hash?.includes("session_id=")) return <CustomerAuthCallback />;
  return (
    <Routes>
      <Route path="/invoice/:order/:token" element={<Invoice />} />
      <Route path="/account" element={<Account />} />
      <Route path="/*" element={<Offers4Bricks />} />
    </Routes>
  );
}

const darkToast = { style: { background: '#F6F6F3', color: '#fff', border: '1px solid #E4E4E0', borderRadius: '2px', fontFamily: 'JetBrains Mono, monospace', fontSize: '12px' } };

function App() {
  const isAdmin = window.location.pathname === "/admin" || window.location.pathname.startsWith("/admin/");
  document.title = isAdmin ? "OFFERS4BRICKS · Admin" : "OFFERS4BRICKS · Instant offers for sealed LEGO® sets";
  if (isAdmin) {
    return (
      <BrowserRouter basename="/admin">
        <AdminRouter />
        <Toaster theme="light" position="bottom-right" toastOptions={darkToast} />
      </BrowserRouter>
    );
  }
  return (
    <BrowserRouter>
      <PublicRouter />
    </BrowserRouter>
  );
}

export default App;
